import { ApolloError } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';
import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as graphql from '@treams/treams-network';
import { useToasts } from '@treams/treams-ui';

import { MAX_FIELDS } from './ConversationRevealFieldPreview';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const ACCEPTED_FILE_TYPES = ['.xlsx', XLSX_MIME];

const MAX_FILE_SIZE = 10_000_000;
const MAX_FILE_SIZE_LABEL = '10 MB';

const UPLOAD_TIMEOUT_MS = 60_000;

export enum UploadStep {
	SELECT = 'select',
	FIELDS = 'fields',
	SUMMARY = 'summary',
}

export type PendingRevealUpload = {
	objectKey: string;
	selectedFields: string[];
	excludedUserIDs: string[];
	fieldTypes: Record<string, graphql.RevealFieldType>;
	matchedCount: number;
	fileName: string;
};

export const toFieldTypeInput = (fieldTypes: Record<string, graphql.RevealFieldType>) =>
	Object.entries(fieldTypes).map(([label, type]) => ({ label, type }));

export type RevealFieldPreview = {
	label: string;
	type: graphql.RevealFieldType;
	example?: string | null;
};

export type RevealUploadSummary = {
	matchedCount: number;
	skippedCount: number;
	/** The header of the column rows were matched on. */
	matchColumn: string;
	detectedFields: string[];
	fieldPreviews: RevealFieldPreview[];
	skippedKeys: string[];
	matchedUsers: {
		user: {
			id: string;
			firstName: string;
			lastName: string;
			fullName: string;
			avatar?: { source: { url: string } } | null;
		};
		values: { label: string; value: string; type: graphql.RevealFieldType }[];
	}[];
};

export function useConversationRevealUpload({
	evaluationFormID,
	onComplete,
	onClose,
	onBusyChange,
}: {
	evaluationFormID?: string;
	onComplete?: (pending: PendingRevealUpload) => void;
	onClose: () => void;
	onBusyChange?: (busy: boolean) => void;
}) {
	const isExcelFile = (file: File) =>
		file.name.toLowerCase().endsWith('.xlsx') || file.type === XLSX_MIME;

	const { t } = useTranslation();
	const { addToast } = useToasts();
	const inputRef = useRef<HTMLInputElement>(null);
	const dragDepth = useRef(0);

	const [getUploadUrl] = graphql.useGetRevealUploadUrlMutation();
	const [parseUpload] = graphql.useParseRevealUploadMutation();
	const [saveUpload] = graphql.useSaveRevealUploadMutation();

	const [step, setStep] = useState<UploadStep>(UploadStep.SELECT);
	const [file, setFile] = useState<File | undefined>(undefined);
	const [objectKey, setObjectKey] = useState<string | undefined>(undefined);
	const [summary, setSummary] = useState<RevealUploadSummary | undefined>(undefined);
	const [selectedFields, setSelectedFields] = useState<string[]>([]);
	const [fieldTypes, setFieldTypes] = useState<Record<string, graphql.RevealFieldType>>({});
	const [excludedUserIDs, setExcludedUserIDs] = useState<string[]>([]);
	const [dragging, setDragging] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<GraphQLFormattedError | string | undefined>(undefined);

	const setBusy = (busy: boolean) => {
		setLoading(busy);
		onBusyChange?.(busy);
	};

	const handleError = (caught: unknown) => {
		const graphQLError = caught instanceof ApolloError ? caught.graphQLErrors[0] : undefined;

		if (graphQLError?.extensions?.error) {
			setError(graphQLError);
		} else {
			setError(t('conversations.reveal.upload.error'));
		}
	};

	const selectFiles = (files?: FileList | null) => {
		if (!files || files.length === 0) {
			return;
		}

		if (files.length > 1) {
			setError(t('conversations.reveal.upload.multipleFiles'));
			return;
		}

		const selected = files[0];

		if (!isExcelFile(selected)) {
			setError(t('conversations.reveal.upload.invalidFileType'));
			return;
		}

		if (selected.size > MAX_FILE_SIZE) {
			setError(t('conversations.reveal.upload.fileTooLarge', { max: MAX_FILE_SIZE_LABEL }));
			return;
		}

		setFile(selected);
		setError(undefined);
	};

	const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		selectFiles(event.target.files);
		event.target.value = '';
	};

	const handleBrowseClick = () => {
		inputRef.current?.click();
	};

	const handleDragEnter = () => {
		dragDepth.current += 1;
		setDragging(true);
	};

	const handleDragOver = (event: DragEvent) => {
		event.preventDefault();
	};

	const handleDragLeave = () => {
		dragDepth.current -= 1;
		if (dragDepth.current <= 0) {
			dragDepth.current = 0;
			setDragging(false);
		}
	};

	const handleDrop = (event: DragEvent) => {
		event.preventDefault();
		dragDepth.current = 0;
		setDragging(false);
		selectFiles(event.dataTransfer.files);
	};

	const handleToggleField = (field: string) => {
		setSelectedFields((current) =>
			current.includes(field) ? current.filter((f) => f !== field) : [...current, field],
		);
	};

	const handleChangeFieldType = (field: string, type: graphql.RevealFieldType) => {
		setFieldTypes((current) => ({ ...current, [field]: type }));
	};

	const handleToggleUser = (userID: string) => {
		setExcludedUserIDs((current) =>
			current.includes(userID) ? current.filter((id) => id !== userID) : [...current, userID],
		);
	};

	const handleBack = () => {
		if (step === UploadStep.SUMMARY) {
			setStep(UploadStep.FIELDS);
			return;
		}

		setStep(UploadStep.SELECT);
		setFile(undefined);
		setObjectKey(undefined);
		setSummary(undefined);
		setSelectedFields([]);
		setFieldTypes({});
		setExcludedUserIDs([]);
		setError(undefined);
	};

	const handleNext = () => {
		setStep(UploadStep.SUMMARY);
	};

	const handleProcess = async () => {
		if (!file) {
			return;
		}

		setBusy(true);
		setError(undefined);

		try {
			const created = await getUploadUrl({ variables: { contentLength: file.size } });
			const upload = created.data?.getRevealUploadUrl;

			if (!upload) {
				throw new Error();
			}

			const headers = new Headers();
			for (const { name, values } of upload.uploadHeaders) {
				for (const value of values) {
					headers.append(name, value);
				}
			}

			const put = await fetch(upload.uploadUrl, {
				method: 'PUT',
				headers,
				body: file,
				signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
			});
			if (!put.ok) {
				throw new Error(put.statusText);
			}

			const parsed = await parseUpload({ variables: { objectKey: upload.objectKey } });
			const result = parsed.data?.parseRevealUpload;

			if (!result) {
				throw new Error();
			}

			setObjectKey(upload.objectKey);
			setSummary(result);
			setSelectedFields([]);
			setFieldTypes(
				Object.fromEntries(result.fieldPreviews.map((preview) => [preview.label, preview.type])),
			);
			setStep(UploadStep.FIELDS);
		} catch (caught) {
			handleError(caught);
		} finally {
			setBusy(false);
		}
	};

	const getIncludedCount = () => {
		const excluded = new Set(excludedUserIDs);
		return (
			summary?.matchedUsers.filter(
				(row) =>
					!excluded.has(row.user.id) &&
					row.values.some((value) => selectedFields.includes(value.label)),
			).length ?? 0
		);
	};

	const handleSave = async ({ force = false }: { force?: boolean } = {}) => {
		if (!objectKey) {
			return;
		}

		// Mirror the backend count: only rows that carry a value for at least
		// one selected field end up being saved. Empty saves wipe live data —
		// require an explicit confirm (force) before proceeding.
		const matchedCount = getIncludedCount();
		if (matchedCount === 0 && !force) {
			return false;
		}

		if (onComplete) {
			onComplete({
				objectKey,
				selectedFields,
				excludedUserIDs,
				fieldTypes,
				matchedCount,
				fileName: file?.name ?? '',
			});
			onClose();
			return true;
		}

		if (!evaluationFormID) {
			return false;
		}

		setBusy(true);
		setError(undefined);

		try {
			const saved = await saveUpload({
				variables: {
					objectKey,
					evaluationFormID,
					selectedFields,
					excludedUserIDs,
					fieldTypes: toFieldTypeInput(fieldTypes),
				},
				refetchQueries: ['EvaluationFormRevealUpload'],
			});
			addToast(
				t('conversations.reveal.upload.saved', {
					count: saved.data?.saveRevealUpload.matchedCount ?? 0,
				}),
				{ appearance: 'success', autoDismiss: true },
			);
			onClose();
			return true;
		} catch (caught) {
			handleError(caught);
			throw caught;
		} finally {
			setBusy(false);
		}
	};

	const canProcess = file !== undefined && !loading;
	const canContinue = selectedFields.length > 0 && selectedFields.length <= MAX_FIELDS && !loading;
	const canSave = canContinue;
	const wouldWipeOnSave = getIncludedCount() === 0;

	return {
		inputRef,
		step,
		file,
		summary,
		selectedFields,
		fieldTypes,
		excludedUserIDs,
		dragging,
		loading,
		error,
		canProcess,
		canContinue,
		canSave,
		wouldWipeOnSave,
		handleFileInputChange,
		handleBrowseClick,
		handleDragEnter,
		handleDragOver,
		handleDragLeave,
		handleDrop,
		handleToggleField,
		handleChangeFieldType,
		handleToggleUser,
		handleBack,
		handleNext,
		handleProcess,
		handleSave,
	};
}
